import { useInfiniteQuery, useQuery } from "react-query";
import { client } from "helpers/client";
import moment from "moment";

export interface Ticket {
  id: string;
  title: string;
  serial: string;
  statusId: number;
  status: string;
  createDate: string;
  updateDate: string;
  description: string;
  product: string;
  subject: string;
  priorityId: number;
  totalAttachments: number;
  topicId:number,
  totalMessages: number;
  owners: Owner[];
  priority: string;
  attachments: Attachment[];
  agent: {
    name: string;
    title: string;
    image: Attachment;
  };
}

export interface Attachment {
  fileName: string;
  mimeType: string;
  fileBase64: string;
}

export interface Owner {
  id: string;
  name: string;
  title: string;
}

export interface TicketLog {
  Id: string;
  EventId: number;
  LogDate: string;
  EventBy: string;
  IsChangeByCustomer: boolean;
  ToAgent: string | null;
}

export interface TicketMessage {
  id: string;
  isCustomerMessage: boolean;
  senderName: string;
  senderImage: string | null;
  message: string;
  createDate: string;
  hasAttachment: boolean;
  attachment: Attachment;
}

export const TicketStatus = {
  Closed: 5,
  Cancelled: 6,
  Active: 1000
};

export const DateFilter: Record<string, { from: any; to: any }> = {
  all: { from: 0, to: 0 },
  today: { from: moment().startOf("day").toDate(), to: moment().endOf("day").toDate() },
  yesterday: {
    from: moment().subtract(1, "days").startOf("day").toDate(),
    to: moment().subtract(1, "days").endOf("day").toDate()
  },
  lastWeek: {
    from: moment().subtract(1, "weeks").startOf("week").toDate(),
    to: moment().subtract(1, "weeks").endOf("week").toDate()
  },
  lastMonth: {
    from: moment().subtract(1, "months").startOf("month").toDate(),
    to: moment().subtract(1, "months").endOf("month").toDate()
  }
};

export enum TicketTopicEnum {
  TechnicalSupport = 1,
  Billing = 2
}

export interface Subject {
  PriorityId: number;
  id: string;
  name: string;
}

export enum EventStatus {
  Created = 1,
  Assigned = 2,
  Reopened = 3,
  Closed = 4,
  Cancelled = 5
}

export enum PriorityStatus {
  High = 1,
  Normal = 2,
  Low = 3
}

export interface CustomerTicketsParams {
  PageNumber: number;
  PageSize: number;
  StatusId: number;
  Search: string;
  FromDate: string;
  ToDate: string;
}

export function useTickets(params?: CustomerTicketsParams) {
  const { data, ...rest } = useInfiniteQuery(
    ["tickets", params],
    async ({ pageParam = 1 }) => {
      const { response } = await client<string>("/ticket/getCustomerTickets", {
        params: {
          ...params,
          PageNumber: pageParam
        }
      });
      if (response) return JSON.parse(response) as { tickets: Partial<Ticket>[]; totalCount: number };
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        const numberOfPages = lastPage && params ? lastPage.totalCount / params.PageSize : undefined;
        if (!numberOfPages) return undefined;
        return numberOfPages > allPages.length ? allPages.length + 1 : undefined;
      }
    }
  );

  return { tickets: data?.pages ?? [], totalCount: data?.pages?.[0]?.totalCount ?? 0, ...rest };
}

export function useTicketDetail(id: string) {
  const { data, ...rest } = useQuery(["ticket", id], async () => {
    const { response } = await client<Ticket>("/ticket/getTicketInfo", { params: { ticketId: id } });
    if (response) return response;
  },{
    enabled:!!id
  });

  return { ticket: data, ...rest };
}

export function useTicketMessages(id: string, pageSize = 5) {
  const { data, ...rest } = useInfiniteQuery(
    ["ticket-messages", id],
    async ({ pageParam = 1 }) => {
      const { response } = await client<string>("/ticket/getTicketMessages", {
        params: {
          ticketId: id,
          pageNumber: pageParam,
          pageSize
        }
      });
      if (response) return JSON.parse(response) as { messages: TicketMessage[]; totalCount: number };
    },
    {
      enabled:!!id,
      getNextPageParam: (lastPage, allPages) => {
        const numberOfPages = lastPage ? lastPage.totalCount / pageSize : undefined;
        if (!numberOfPages) return undefined;
        return numberOfPages > allPages.length ? allPages.length + 1 : undefined;
      }
    }
  );

  // TODO: change total count
  return { messages: data?.pages ?? [], totalCount: data?.pages?.[0]?.totalCount ?? 0, ...rest };
}
